import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { DhlAccountSettingsOutDTO } from '@api/ringil-api.v2';
import { Field } from '@components/framework/formfield/Field.tsx';
import { InputText } from 'primereact/inputtext';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgInfoCircle from '@icons/info.svg?react';
import { Callbacks } from '@api/apiRuntime';

interface ComponentProps {
  companyId: string;
}

/**
 * DHL Express API credentials (MyDHL API username + password) of the company.
 * The password is write-only - the backend only reports whether one is stored;
 * leaving the field empty keeps the stored password.
 */
const CompanyProfileDhl: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const { getDhlAccountSettings, saveDhlAccountSettings, deleteDhlAccountSettings } = useContext(ApiContext);

  const [settings, setSettings] = useState<DhlAccountSettingsOutDTO | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [action, setAction] = useState<'save' | 'delete' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (data: DhlAccountSettingsOutDTO) => {
    setSettings(data);
    setUsername(data.username ?? '');
    setPassword('');
  };

  useEffect(() => {
    getDhlAccountSettings(apply);
  }, [companyId]);

  // VALIDATION-type API errors are delivered via onValidationFailed and would be silently swallowed otherwise
  const callbacks: Callbacks = {
    onValidationFailed: (response: any) => {
      const firstMessage = Array.isArray(response) ? response[0] : null;
      setError(firstMessage?.message ?? 'api.dhl.settings.error');
      setAction(null);
    },
    onError: (_, showStandardError) => {
      showStandardError();
      setAction(null);
    },
  };

  const handleSave = () => {
    setError(null);
    setAction('save');
    saveDhlAccountSettings(
      { username: username.trim(), password: password || undefined },
      data => {
        apply(data);
        setAction(null);
      },
      null,
      callbacks,
    );
  };

  const handleDelete = () => {
    setError(null);
    setAction('delete');
    deleteDhlAccountSettings(
      data => {
        apply(data);
        setConfirmDelete(false);
        setAction(null);
      },
      null,
      callbacks,
    );
  };

  if (settings == null) return <></>;

  const formValid = !!username.trim() && (settings.passwordSet || !!password);

  return (
    <div className="flex flex-col gap-4 mt-4 max-w-[700px]">
      <COHeadline variant={'h3'} title={tr('CompanyProfileDhl.title', 'DHL Express API')} />

      <COInlineMessage variant={settings.configured ? 'success' : 'info'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
        {settings.configured
          ? tr('CompanyProfileDhl.configured', 'DHL Express API credentials are set (user {{username}}). Prices and shipments are handled through this account.', {
              username: settings.username,
            })
          : tr('CompanyProfileDhl.notConfigured', 'Enter the username and password of your DHL Express (MyDHL) API account.')}
      </COInlineMessage>

      {error && (
        <COInlineMessage variant={'error'}>
          {tr(error, error)}
        </COInlineMessage>
      )}

      <Field label={tr('CompanyProfileDhl.username', 'Username')} required>
        <InputText value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" style={{ width: '100%' }} />
      </Field>
      <Field label={tr('CompanyProfileDhl.password', 'Password')} required={!settings.passwordSet}>
        <InputText
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoComplete="new-password"
          placeholder={settings.passwordSet ? tr('CompanyProfileDhl.passwordUnchanged', 'Leave empty to keep the stored password') : undefined}
          style={{ width: '100%' }}
        />
      </Field>

      <div className="flex flex-row gap-3 items-center">
        <RingilButton
          mode={'filled-rectangle'}
          label={tr('CompanyProfileDhl.save', 'Save')}
          onClick={handleSave}
          loading={action === 'save'}
          disabled={!formValid || action != null}
        />
        {settings.configured && !confirmDelete && (
          <RingilButton
            mode={'outlined-rectangle'}
            label={tr('CompanyProfileDhl.remove', 'Remove credentials')}
            onClick={() => setConfirmDelete(true)}
            disabled={action != null}
          />
        )}
        {confirmDelete && (
          <>
            <span className="text-sm text-content-secondary">
              {tr('CompanyProfileDhl.removeConfirm', 'Really remove the DHL credentials?')}
            </span>
            <RingilButton
              mode={'filled-rectangle'}
              label={tr('CompanyProfileDhl.removeYes', 'Remove')}
              onClick={handleDelete}
              loading={action === 'delete'}
              disabled={action != null}
            />
            <RingilButton
              mode={'text-only'}
              label={tr('CompanyProfileDhl.removeCancel', 'Cancel')}
              onClick={() => setConfirmDelete(false)}
              disabled={action != null}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyProfileDhl;
